/**
 * Persona context: the deterministic first-person text we put in front of a
 * prompt when it runs "as" a persona.
 *
 * Pure and dependency-free on purpose (client-safe), so nothing here may
 * import from the app, the lab, or a server-only package.
 *
 * A persona is one of two asker types:
 *  - "professional": someone asking on behalf of an organization. The text
 *    pre-answers company size, industry, region, current tools, the asker's
 *    part in a purchase, priorities and constraints. Every persona created
 *    before asker types existed is professional, and this rendering is
 *    byte-identical to what it has always been.
 *  - "individual": someone asking for themselves, their household or someone
 *    they care for. The text says who they are, where, a sentence about them,
 *    what they already have, what matters most and their constraints.
 *
 * It is factual on purpose; opinionated phrasing over-steers the answer.
 */

/* ------------------------------------------------------------------ */
/* Field vocabularies                                                  */
/* ------------------------------------------------------------------ */

/**
 * Who is asking. "individual": a person asking for themselves, their
 * household or someone they care for. "professional": someone asking on
 * behalf of an organization (every persona created before asker types).
 */
export const PERSONA_ASKER_TYPES = ["individual", "professional"] as const;
export type PersonaAskerType = (typeof PERSONA_ASKER_TYPES)[number];

export const PERSONA_ASKER_TYPE_LABELS: Record<PersonaAskerType, string> = {
	individual: "A person",
	professional: "Someone at an organization",
};

/** The asker type of a persona; a missing or unknown value is professional. */
export function personaAskerType(
	fields: Pick<PersonaFields, "askerType">,
): PersonaAskerType {
	return fields.askerType === "individual" ? "individual" : "professional";
}

export const PERSONA_ROLE_GROUPS = [
	"finance",
	"it",
	"operations",
	"executive",
	"end_user",
	"procurement",
	"marketing",
	"sales",
	"other",
] as const;
export type PersonaRoleGroup = (typeof PERSONA_ROLE_GROUPS)[number];

export const PERSONA_ROLE_GROUP_LABELS: Record<PersonaRoleGroup, string> = {
	finance: "Finance",
	it: "IT",
	operations: "Operations",
	executive: "Executive",
	end_user: "End user",
	procurement: "Procurement",
	marketing: "Marketing",
	sales: "Sales",
	other: "Other",
};

export const PERSONA_SENIORITIES = [
	"c_level",
	"vp",
	"director",
	"manager",
	"individual_contributor",
] as const;
export type PersonaSeniority = (typeof PERSONA_SENIORITIES)[number];

export const PERSONA_SENIORITY_LABELS: Record<PersonaSeniority, string> = {
	c_level: "C-level",
	vp: "VP",
	director: "Director",
	manager: "Manager",
	individual_contributor: "Individual contributor",
};

export const PERSONA_DECISION_ROLES = [
	"economic_buyer",
	"champion",
	"technical_evaluator",
	"end_user",
	"gatekeeper",
] as const;
export type PersonaDecisionRole = (typeof PERSONA_DECISION_ROLES)[number];

export const PERSONA_DECISION_ROLE_LABELS: Record<PersonaDecisionRole, string> =
	{
		economic_buyer: "Economic buyer",
		champion: "Champion",
		technical_evaluator: "Technical evaluator",
		end_user: "End user",
		gatekeeper: "Gatekeeper",
	};

/** Company-size bands, smallest first. Keys are stable storage values. */
export const PERSONA_COMPANY_SIZES = [
	"1-10",
	"11-50",
	"51-200",
	"201-500",
	"501-1000",
	"1001-5000",
	"5000+",
] as const;
export type PersonaCompanySize = (typeof PERSONA_COMPANY_SIZES)[number];

export const PERSONA_COMPANY_SIZE_LABELS: Record<PersonaCompanySize, string> = {
	"1-10": "1 to 10 employees",
	"11-50": "11 to 50 employees",
	"51-200": "51 to 200 employees",
	"201-500": "201 to 500 employees",
	"501-1000": "501 to 1,000 employees",
	"1001-5000": "1,001 to 5,000 employees",
	"5000+": "More than 5,000 employees",
};

/** How the band reads inside the context sentence ("with …"). */
const COMPANY_SIZE_PHRASES: Record<PersonaCompanySize, string> = {
	"1-10": "fewer than 10 employees",
	"11-50": "about 40 employees",
	"51-200": "about 120 employees",
	"201-500": "about 350 employees",
	"501-1000": "about 750 employees",
	"1001-5000": "about 2,500 employees",
	"5000+": "more than 5,000 employees",
};

const DECISION_ROLE_SENTENCES: Record<PersonaDecisionRole, string> = {
	economic_buyer: "I sign off on the purchase.",
	champion: "I'm leading the evaluation.",
	technical_evaluator: "I run the technical review.",
	end_user: "I'll use the tool every day.",
	gatekeeper: "Every new vendor needs my approval.",
};

/* ------------------------------------------------------------------ */
/* Fields                                                              */
/* ------------------------------------------------------------------ */

/**
 * The structured persona fields that feed the context. Mirrors the `Persona`
 * model (identity, organization or household, situation) minus storage
 * columns. An individual uses title, segment, region, household, techStack,
 * priorities and constraints; the organization fields (roleGroup, seniority,
 * decisionRole, companySize, industry) are null for them.
 */
export interface PersonaFields {
	/** Missing means "professional" (every persona created before asker types). */
	askerType?: PersonaAskerType | null;
	/**
	 * Optional name a user chose to add, e.g. "Sam". Never required, never
	 * generated, never sent. Display is role-first: see personaDisplayLabel.
	 */
	name?: string | null;
	/**
	 * Who they are. Professional: role and title, e.g. "CFO" or "Director of
	 * IT". Individual: a plain description, e.g. "Retired homeowner".
	 */
	title?: string | null;
	/** Professionals only; null for an individual. */
	roleGroup?: PersonaRoleGroup | null;
	seniority?: PersonaSeniority | null;
	decisionRole?: PersonaDecisionRole | null;
	/** One of the brand snapshot's `icp.segments`. Used for grouping only. */
	segment?: string | null;
	companySize?: PersonaCompanySize | null;
	industry?: string | null;
	region?: string | null;
	/**
	 * Individuals only: one first-person sentence that adds age, life stage or
	 * who they live with or care for ("I'm in my 70s and live with my wife.").
	 */
	household?: string | null;
	/**
	 * What the asker already has or uses: a professional's software tools, an
	 * individual's coverage, products, home or equipment.
	 */
	techStack?: readonly string[];
	priorities?: readonly string[];
	constraints?: readonly string[];
	/** When set, sent instead of the rendered text (still capped). */
	contextOverride?: string | null;
}

export const PERSONA_CONTEXT_MAX_CHARS = 600;
/** DataForSEO's ChatGPT/Gemini scrapers take one keyword of at most 2,000 characters. */
export const PERSONA_QUERY_MAX_CHARS = 2000;

/* ------------------------------------------------------------------ */
/* Rendering                                                           */
/* ------------------------------------------------------------------ */

/** Collapse every whitespace run (newlines included) to one space. */
function oneLine(text: string): string {
	return text.replace(/\s+/g, " ").trim();
}

/** Lower-case a leading word unless it is an acronym or a product name. */
function lowerFirst(text: string): string {
	const t = oneLine(text).replace(/[.;,]+$/, "");
	const firstWord = t.split(" ")[0] ?? "";
	// "SSO", "NetSuite", "B2B", "SOX": keep casing when the first word has
	// a capital or a digit after its first letter.
	if (firstWord === "I" || /[A-Z0-9]/.test(firstWord.slice(1))) {
		return t;
	}
	return t.charAt(0).toLowerCase() + t.slice(1);
}

function article(word: string): "a" | "an" {
	return /^[aeiou]/i.test(word) ? "an" : "a";
}

/** Oxford-comma list: "A", "A and B", "A, B, and C". */
function joinList(items: readonly string[]): string {
	if (items.length <= 1) {
		return items[0] ?? "";
	}
	if (items.length === 2) {
		return `${items[0]} and ${items[1]}`;
	}
	return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function regionPhrase(region: string): string {
	const r = oneLine(region);
	return /^(United |Netherlands|Philippines|UAE)/.test(r) ? `the ${r}` : r;
}

const SENIOR_SEATS: ReadonlySet<PersonaSeniority> = new Set([
	"c_level",
	"vp",
	"director",
]);

/**
 * Industries that already name the kind of organization ("Marketing agency",
 * "Law firm") read as "at a marketing agency", not "at a marketing agency
 * company".
 */
const ORGANIZATION_NOUN =
	/\b(agency|firm|studio|company|business|practice|startup|consultancy|group|organization|nonprofit|retailer|manufacturer|publisher|marketplace|bank|lab|clinic|hospital|school|university|network)$/i;

function identitySentence(f: PersonaFields): string | null {
	const title = f.title ? oneLine(f.title) : null;
	const industry = f.industry ? oneLine(f.industry) : null;
	const who = title
		? `I'm ${f.seniority && SENIOR_SEATS.has(f.seniority) ? "the" : article(title)} ${title}`
		: "I work";
	const company = industry
		? `at ${article(industry)} ${lowerFirst(industry)}${ORGANIZATION_NOUN.test(industry) ? "" : " company"}`
		: "at a company";
	const where = f.region ? ` in ${regionPhrase(f.region)}` : "";
	const size = f.companySize
		? ` with ${COMPANY_SIZE_PHRASES[f.companySize]}`
		: "";
	if (!title && !industry && !f.region && !f.companySize) {
		return null;
	}
	return `${who} ${company}${where}${size}.`;
}

interface Lists {
	techStack: string[];
	priorities: string[];
	constraints: string[];
}

function buildSentences(
	f: PersonaFields,
	lists: Lists,
	includeRole: boolean,
): string[] {
	const out: string[] = [];
	const identity = identitySentence(f);
	if (identity) {
		out.push(identity);
	}
	if (includeRole && f.decisionRole) {
		out.push(DECISION_ROLE_SENTENCES[f.decisionRole]);
	}
	if (lists.techStack.length > 0) {
		out.push(`We use ${joinList(lists.techStack)}.`);
	}
	if (lists.priorities.length > 0) {
		// "My top priorities:" reads well for verb phrases ("close the books
		// faster") and noun phrases ("SSO on every tool") alike.
		out.push(
			`My top priorities: ${lists.priorities.map((x) => listItemInSentence(x, f.region)).join("; ")}.`,
		);
	}
	if (lists.constraints.length > 0) {
		out.push(
			`Constraints to keep in mind: ${lists.constraints.map((x) => listItemInSentence(x, f.region)).join("; ")}.`,
		);
	}
	return out;
}

/** Cut at the last word boundary that fits, ending on a full stop. */
function hardCap(text: string, max: number): string {
	if (text.length <= max) {
		return text;
	}
	const cut = text.slice(0, max - 1);
	const lastSpace = cut.lastIndexOf(" ");
	const base = (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(
		/[\s,;:]+$/,
		"",
	);
	return `${base}.`;
}

/* ---------- individuals ---------- */

/**
 * Words that open an individual's title and keep their capital ("a Medicare
 * senior", "a Spanish-speaking parent"). lowerFirst already keeps acronyms
 * and inner-capital words ("a VA patient").
 */
const PROPER_NOUN_FIRST_WORDS: ReadonlySet<string> = new Set([
	"Medicare",
	"Medicaid",
	"Medigap",
	"Gen",
	"American",
	"Canadian",
	"British",
	"English",
	"Spanish",
	"French",
	"German",
	"Italian",
	"Chinese",
	"Japanese",
	"Korean",
	"Vietnamese",
	"Filipino",
	"Indian",
	"Mexican",
	"Latino",
	"Latina",
	"Hispanic",
	"Asian",
	"African",
	"European",
	"Scandinavian",
	"Nordic",
	"Swedish",
	"Norwegian",
	"Danish",
	"Finnish",
	"Dutch",
	"Irish",
	"Scottish",
	"Welsh",
	"Australian",
	"Brazilian",
	"Portuguese",
	"Polish",
	"Greek",
	"Caribbean",
	"Christian",
	"Catholic",
	"Jewish",
	"Muslim",
	"Hindu",
	"Buddhist",
]);

/** "Medicare senior" stays; "Busy parent" → "busy parent". */
function titleInSentence(title: string): string {
	const t = oneLine(title).replace(/[.;,]+$/, "");
	const firstWord = (t.split(" ")[0] ?? "").split("-")[0] ?? "";
	return PROPER_NOUN_FIRST_WORDS.has(firstWord) ? t : lowerFirst(t);
}

/**
 * A priority or constraint, lower-cased at its first letter unless it opens
 * with a proper noun: a place in the persona's region ("Maine bankruptcy
 * rules" for a persona in Portland, Maine) or a PROPER_NOUN_FIRST_WORDS entry.
 */
function listItemInSentence(
	item: string,
	region: string | null | undefined,
): string {
	const t = oneLine(item).replace(/[.;,]+$/, "");
	const firstWord = (t.split(" ")[0] ?? "").split("-")[0] ?? "";
	const regionWords = new Set(
		(region ?? "").split(/[^A-Za-z]+/).filter((w) => /^[A-Z]/.test(w)),
	);
	return PROPER_NOUN_FIRST_WORDS.has(firstWord) || regionWords.has(firstWord)
		? t
		: lowerFirst(t);
}

/** End a sentence with a full stop unless it already ends with punctuation. */
function asSentence(text: string): string {
	const t = oneLine(text);
	return /[.!?]$/.test(t) ? t : `${t}.`;
}

function individualSentences(f: PersonaFields, lists: Lists): string[] {
	const out: string[] = [];
	const title = f.title ? titleInSentence(f.title) : "";
	const where = f.region ? regionPhrase(f.region) : "";
	if (title) {
		out.push(
			`I'm ${article(title)} ${title}${where ? ` in ${where}` : ""}.`,
		);
	} else if (where) {
		out.push(`I live in ${where}.`);
	}
	const household = f.household ? oneLine(f.household) : "";
	if (household) {
		out.push(asSentence(household));
	}
	if (lists.techStack.length > 0) {
		out.push(`I already have ${joinList(lists.techStack)}.`);
	}
	if (lists.priorities.length > 0) {
		out.push(
			`What matters most: ${lists.priorities.map((x) => listItemInSentence(x, f.region)).join("; ")}.`,
		);
	}
	if (lists.constraints.length > 0) {
		out.push(
			`Constraints to keep in mind: ${lists.constraints.map((x) => listItemInSentence(x, f.region)).join("; ")}.`,
		);
	}
	return out;
}

/* ---------- both ---------- */

/**
 * Render the persona's first-person context: one line, at most
 * PERSONA_CONTEXT_MAX_CHARS characters, deterministic for the same fields.
 *
 * When the full text is too long it trims list items from the end (the
 * longest list first), then (professionals) drops the decision-role
 * sentence, then cuts at a word boundary. An override is normalised to one
 * line and capped the same way.
 */
export function renderPersonaContext(fields: PersonaFields): string {
	const override = fields.contextOverride
		? oneLine(fields.contextOverride)
		: "";
	if (override) {
		return hardCap(override, PERSONA_CONTEXT_MAX_CHARS);
	}
	if (personaAskerType(fields) === "individual") {
		return renderIndividualContext(fields);
	}

	const lists: Lists = {
		techStack: (fields.techStack ?? []).map(oneLine).filter(Boolean),
		priorities: (fields.priorities ?? []).map(oneLine).filter(Boolean),
		constraints: (fields.constraints ?? []).map(oneLine).filter(Boolean),
	};
	let includeRole = true;
	let text = buildSentences(fields, lists, includeRole).join(" ");

	while (text.length > PERSONA_CONTEXT_MAX_CHARS) {
		const trimmable = (["constraints", "priorities", "techStack"] as const)
			.filter((k) => lists[k].length > 1)
			.sort(
				(a, b) => lists[b].join(" ").length - lists[a].join(" ").length,
			);
		if (trimmable.length > 0) {
			lists[trimmable[0]].pop();
		} else if (includeRole) {
			includeRole = false;
		} else {
			break;
		}
		text = buildSentences(fields, lists, includeRole).join(" ");
	}
	return hardCap(text, PERSONA_CONTEXT_MAX_CHARS);
}

function renderIndividualContext(fields: PersonaFields): string {
	const lists: Lists = {
		techStack: (fields.techStack ?? []).map(oneLine).filter(Boolean),
		priorities: (fields.priorities ?? []).map(oneLine).filter(Boolean),
		constraints: (fields.constraints ?? []).map(oneLine).filter(Boolean),
	};
	let text = individualSentences(fields, lists).join(" ");
	while (text.length > PERSONA_CONTEXT_MAX_CHARS) {
		const trimmable = (["constraints", "priorities", "techStack"] as const)
			.filter((k) => lists[k].length > 1)
			.sort(
				(a, b) => lists[b].join(" ").length - lists[a].join(" ").length,
			);
		if (trimmable.length === 0) {
			break;
		}
		lists[trimmable[0]].pop();
		text = individualSentences(fields, lists).join(" ");
	}
	return hardCap(text, PERSONA_CONTEXT_MAX_CHARS);
}

export interface PersonaQuery {
	/** Exactly what the platform receives: context + question on one line. */
	text: string;
	length: number;
	maxChars: number;
	/** False when the combined text is over PERSONA_QUERY_MAX_CHARS. */
	fits: boolean;
	/** Offset in `text` where the unchanged base question starts. */
	queryStart: number;
}

/**
 * Put the persona context in front of the unchanged base question, on ONE
 * line (the scrapers take a single keyword; a newline is not safe). The
 * caller decides what to do when `fits` is false; this never truncates the
 * question, because a changed question breaks the controlled comparison.
 */
export function applyPersonaToQuery(
	context: string,
	query: string,
): PersonaQuery {
	const ctx = oneLine(context);
	const q = oneLine(query);
	const text = ctx ? `${ctx} ${q}` : q;
	return {
		text,
		length: text.length,
		maxChars: PERSONA_QUERY_MAX_CHARS,
		fits: text.length <= PERSONA_QUERY_MAX_CHARS,
		queryStart: ctx ? ctx.length + 1 : 0,
	};
}

/**
 * Stable short hash of the rendered context (FNV-1a, hex). Stored as
 * `contextHash` so a report can tell which answers ran on which wording.
 */
export function personaContextHash(context: string): string {
	let h = 0x811c9dc5;
	for (let i = 0; i < context.length; i++) {
		h ^= context.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return (h >>> 0).toString(16).padStart(8, "0");
}

/* ------------------------------------------------------------------ */
/* Display (role-first; the name is optional and secondary)            */
/* ------------------------------------------------------------------ */

/** Role label when a persona has no title. */
export const PERSONA_ROLE_GROUP_FALLBACK_TITLES: Record<
	PersonaRoleGroup,
	string
> = {
	finance: "Finance leader",
	it: "IT leader",
	operations: "Operations leader",
	executive: "Executive",
	end_user: "End user",
	procurement: "Procurement lead",
	marketing: "Marketing leader",
	sales: "Sales leader",
	other: "Professional",
};

/** Label for an individual with no title. */
export const PERSONA_INDIVIDUAL_FALLBACK_TITLE = "Person";

/** Trailing nouns that add nothing to a label ("mid-market SaaS companies"). */
const GENERIC_TAIL = /^(companies|company|organizations|organization|orgs)$/i;

function singular(word: string): string {
	if (/ies$/i.test(word)) {
		return `${word.slice(0, -3)}y`;
	}
	if (/(ss|sh|ch|x)es$/i.test(word)) {
		return word.slice(0, -2);
	}
	if (/[^su]s$/i.test(word)) {
		return word.slice(0, -1);
	}
	return word;
}

/**
 * A segment as a short singular qualifier: "Early-stage startups" →
 * "early-stage startup", "Mid-market SaaS companies" → "mid-market SaaS",
 * "Multi-entity businesses" → "multi-entity business".
 */
export function personaSegmentQualifier(segment: string): string {
	const words = lowerFirst(segment).split(" ").filter(Boolean);
	if (words.length === 0) {
		return "";
	}
	const last = words[words.length - 1];
	if (words.length > 1 && GENERIC_TAIL.test(last)) {
		words.pop();
	} else {
		words[words.length - 1] = singular(last);
	}
	return words.join(" ");
}

/** The user-added name, trimmed, or null. Generated personas never have one. */
export function personaDisplayName(
	fields: Pick<PersonaFields, "name">,
): string | null {
	const name = fields.name ? oneLine(fields.name) : "";
	return name.length > 0 ? name : null;
}

/** "201 to 500 employees · B2B SaaS · United States" (empty parts skipped). */
export function personaCompanyLine(fields: PersonaFields): string {
	return [
		fields.companySize
			? PERSONA_COMPANY_SIZE_LABELS[fields.companySize]
			: null,
		fields.industry ? oneLine(fields.industry) : null,
		fields.region ? oneLine(fields.region) : null,
	]
		.filter(Boolean)
		.join(" · ");
}

/**
 * Display label derived from the fields, never from the name. An individual
 * is shown by who they are (their title: "Retired homeowner"). A
 * professional is role-first: "CFO, early-stage startup", "Director of IT,
 * mid-market SaaS", falling back to industry, then company size, when there
 * is no segment, and to a role group title ("Finance leader") when there is
 * no title.
 */
export function personaDisplayLabel(fields: PersonaFields): string {
	if (personaAskerType(fields) === "individual") {
		const title = fields.title ? oneLine(fields.title) : "";
		return title || PERSONA_INDIVIDUAL_FALLBACK_TITLE;
	}
	const role = fields.title
		? oneLine(fields.title)
		: PERSONA_ROLE_GROUP_FALLBACK_TITLES[fields.roleGroup ?? "other"];
	const qualifier = fields.segment
		? personaSegmentQualifier(fields.segment)
		: fields.industry
			? lowerFirst(fields.industry)
			: fields.companySize
				? PERSONA_COMPANY_SIZE_LABELS[fields.companySize]
				: "";
	return qualifier ? `${role}, ${qualifier}` : role;
}

/** An individual's context line: "Watertown, New York · Household prescriptions". */
export function personaIndividualLine(fields: PersonaFields): string {
	return [fields.region, fields.segment]
		.map((v) => (v ? oneLine(v) : ""))
		.filter(Boolean)
		.join(" · ");
}

/**
 * The secondary line under the label: the user-added name when there is one,
 * otherwise the company context (professional) or region and segment
 * (individual).
 */
export function personaDisplaySublabel(fields: PersonaFields): string {
	return (
		personaDisplayName(fields) ??
		(personaAskerType(fields) === "individual"
			? personaIndividualLine(fields)
			: personaCompanyLine(fields))
	);
}

/**
 * Labels for a list, disambiguated: when two personas of the same asker type
 * share a label, each gets the first detail that tells them apart
 * (professional: industry, company size, region; individual: segment,
 * region), else a number. Same order as the input.
 */
export function personaDisplayLabels(list: readonly PersonaFields[]): string[] {
	const base = list.map(personaDisplayLabel);
	const kinds = list.map(personaAskerType);
	const professionalDetails: Array<(f: PersonaFields) => string | null> = [
		(f) => (f.industry ? oneLine(f.industry) : null),
		(f) =>
			f.companySize ? PERSONA_COMPANY_SIZE_LABELS[f.companySize] : null,
		(f) => (f.region ? oneLine(f.region) : null),
	];
	const individualDetails: Array<(f: PersonaFields) => string | null> = [
		(f) => (f.segment ? oneLine(f.segment) : null),
		(f) => (f.region ? oneLine(f.region) : null),
	];
	return base.map((label, i) => {
		const twins = base
			.map((l, j) => (l === label && kinds[j] === kinds[i] ? j : -1))
			.filter((j) => j >= 0);
		const details =
			kinds[i] === "individual" ? individualDetails : professionalDetails;
		if (twins.length < 2) {
			return label;
		}
		for (const detail of details) {
			const values = twins.map((j) => detail(list[j]));
			if (
				values.every((v) => v) &&
				new Set(values).size === twins.length
			) {
				return `${label} (${detail(list[i])})`;
			}
		}
		return `${label} (${twins.indexOf(i) + 1})`;
	});
}
